import { FC } from 'react';
import clsx from 'clsx';
import { FiGlobe, MdOutlineExpandMore } from '@/config/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguageLoading } from '@/store/languageStore';
import type { LanguageSelectorProps } from './LanguageSelector.types';
import styles from './LanguageSelector.module.css';

/**
 * LanguageSelector Component
 * 
 * Dropdown component for switching between Portuguese and English languages
 */
const LanguageSelector: FC<LanguageSelectorProps> = ({
  showNames = false,
  size = 'md',
  className,
}) => {
  const { t, currentLanguage, toggleLanguage } = useTranslation();
  const isLoading = useLanguageLoading();

  const languages = [
    {
      code: 'pt',
      name: 'Português',
      flag: '🇧🇷',
    },
    {
      code: 'en', 
      name: 'English',
      flag: '🇺🇸',
    },
  ];

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  const handleToggle = async () => {
    if (isLoading) return;
    try {
      await toggleLanguage();
    } catch (error) {
      console.error('Failed to toggle language:', error);
    }
  };

  return (
    <div className={clsx(styles.languageSelector, styles[size], className)}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={isLoading}
        className={clsx(styles.trigger, {
          [styles.loading]: isLoading,
        })}
        aria-label={t('theme.selectTheme')}
        title={`${t('common.switch')} ${currentLang?.name}`}
      >
        <div className={styles.currentLanguage}>
          {showNames ? (
            <>
              <FiGlobe className={styles.icon} />
              <span className={styles.languageName}>
                {currentLang?.name}
              </span>
            </>
          ) : (
            <span className={styles.flag} role="img" aria-label={currentLang?.name}>
              {currentLang?.flag}
            </span>
          )}
        </div>
        
        <MdOutlineExpandMore 
          className={clsx(styles.expandIcon, {
            [styles.loading]: isLoading,
          })} 
        />
      </button>

      {/* Language info tooltip */}
      <div className={styles.tooltip}>
        <span>
          {currentLanguage === 'pt' ? 'Clique para English' : 'Click for Português'}
        </span>
      </div>
    </div>
  );
};

export default LanguageSelector;